import { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import "./BidsRecords.css";

const LIMIT = 20;

const FILTER_FIELDS = [
    { key: "bid_amount",       label: "Bid Amount",         type: "text", placeholder: "e.g. 1100" },
    { key: "username",         label: "Username",           type: "text", placeholder: "Search username" },
    { key: "lot_number",       label: "Lot Number",         type: "text", placeholder: "e.g. 47033456" },
    { key: "auction_name",     label: "Auction",            type: "text", placeholder: "e.g. copart" },
    { key: "bid_type",         label: "Bid Type",           type: "text", placeholder: "e.g. pre-bid" },
    { key: "record_time_from", label: "Record Time (from)", type: "date" },
    { key: "record_time_to",   label: "Record Time (to)",   type: "date" },
];

const EMPTY_FILTERS = Object.fromEntries(FILTER_FIELDS.map(f => [f.key, ""]));

const BidsRecords = () => {
    const history = useHistory();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const setSearchParams = (updater) => {
        const next = typeof updater === "function" ? updater(new URLSearchParams(location.search)) : updater;
        history.push({ search: next.toString() });
    };
    const [records, setRecords] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showFilter, setShowFilter] = useState(false);
    const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);

    const page = parseInt(searchParams.get("page") || "1");
    const appliedFilters = Object.fromEntries(FILTER_FIELDS.map(f => [f.key, searchParams.get(f.key) || ""]));
    const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
    const totalPages = Math.ceil(total / LIMIT);

    useEffect(() => {
        setLoading(true);
        setError("");
        const params = new URLSearchParams({ page, limit: LIMIT });
        FILTER_FIELDS.forEach(f => { const v = searchParams.get(f.key); if (v) params.set(f.key, v); });
        axios.get(`/api/bids-records?${params}`)
            .then((res) => {
                if (Array.isArray(res.data.data)) {
                    setRecords(res.data.data);
                    setTotal(res.data.total ?? 0);
                } else {
                    setRecords([]);
                    setTotal(0);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [searchParams, page]);

    const setPage = (p) => {
        setSearchParams(prev => { const next = new URLSearchParams(prev); next.set("page", String(p)); return next; });
    };

    const handleOpenFilter = () => {
        setFilterDraft({ ...appliedFilters });
        setShowFilter(true);
    };

    const handleApply = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set("page", "1");
            FILTER_FIELDS.forEach(f => {
                filterDraft[f.key] ? next.set(f.key, filterDraft[f.key]) : next.delete(f.key);
            });
            return next;
        });
        setShowFilter(false);
    };

    const handleClear = () => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set("page", "1");
            FILTER_FIELDS.forEach(f => next.delete(f.key));
            return next;
        });
        setFilterDraft(EMPTY_FILTERS);
        setShowFilter(false);
    };

    const formatDate = (val) => {
        if (!val) return "—";
        const d = new Date(val);
        return isNaN(d) ? val : d.toLocaleString();
    };

    const BID_TYPES = { live: "live", preBid: "pre-bid" };
    const AUCTIONS = { iaai: "iaai" };

    const BID_TYPE_COLORS = {
        [BID_TYPES.live]:   "#10b981",
        [BID_TYPES.preBid]: "#6366f1",
    };

    const AUCTION_COLORS = {
        [AUCTIONS.iaai]: "#f59e0b",
    };

    const bidTypeBadge = (type) => {
        if (!type) return "—";
        if (type.includes("__ERROR__")) return <span className="br-badge" style={{ background: "#ef4444" }}>{type}</span>;
        const bg = BID_TYPE_COLORS[type.toLowerCase()] || "#64748b";
        return <span className="br-badge" style={{ background: bg }}>{type}</span>;
    };

    const auctionBadge = (name) => {
        if (!name) return "—";
        const bg = AUCTION_COLORS[name.toLowerCase()] || "#64748b";
        return <span className="br-badge" style={{ background: bg, textTransform: "uppercase", width: 120 }}>{name}</span>;
    };

    return (
        <div className="br-wrapper">
            <div className="br-header">
                <h2 className="br-title">Bids Records</h2>
                <span className="br-total-badge">{total} total</span>
                <button
                    className={`br-filter-btn${activeFilterCount ? " br-filter-btn-active" : ""}`}
                    onClick={handleOpenFilter}
                >
                    Filters {activeFilterCount > 0 && <span className="br-filter-count">{activeFilterCount}</span>}
                </button>
            </div>

            {error && <div className="br-error">{error}</div>}

            {showFilter && (
                <div className="br-modal-overlay" onClick={() => setShowFilter(false)}>
                    <div className="br-modal" onClick={e => e.stopPropagation()}>
                        <div className="br-modal-header">
                            <span>Filters</span>
                            <button className="br-modal-close" onClick={() => setShowFilter(false)}>✕</button>
                        </div>
                        <div className="br-modal-body">
                            {FILTER_FIELDS.map(f => (
                                <div key={f.key} className="br-filter-row">
                                    <label className="br-filter-label">{f.label}</label>
                                    <input
                                        className="br-filter-input"
                                        type={f.type}
                                        placeholder={f.placeholder || ""}
                                        value={filterDraft[f.key]}
                                        onChange={e => setFilterDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="br-modal-footer">
                            <button className="br-filter-clear-btn" onClick={handleClear}>Clear all</button>
                            <button className="br-filter-apply-btn" onClick={handleApply}>Apply</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="br-card">
                <div className="br-table-wrap">
                    <table className="br-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th align="center">Bid Amount</th>
                                <th align="center">Username</th>
                                <th align="center">Lot Number</th>
                                <th align="center">Auction</th>
                                <th align="center">Bid Type</th>
                                <th align="center">Record Time</th>
                                <th align="center">Created At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="br-loading">
                                        <span className="br-spinner" />
                                        Loading...
                                    </td>
                                </tr>
                            ) : records.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="br-empty">No records found</td>
                                </tr>
                            ) : (
                                records.map((r) => (
                                    <tr key={r.id}>
                                        <td className="br-id">{r.id}</td>
                                        <td className="br-amount">${Number(r.bid_amount).toFixed(2)}</td>
                                        <td>{r.username || "—"}</td>
                                        <td>{r.lot_number || "—"}</td>
                                        <td>{auctionBadge(r.auction_name)}</td>
                                        <td>{bidTypeBadge(r.bid_type)}</td>
                                        <td>{formatDate(r.record_time)}</td>
                                        <td>{formatDate(r.created_at)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalPages > 1 && (
                    <div className="br-pagination">
                        <button className="br-page-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                        <button className="br-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((p) => p >= page - 2 && p <= page + 2)
                            .map((p) => (
                                <button
                                    key={p}
                                    className={`br-page-btn ${p === page ? "br-page-active" : ""}`}
                                    onClick={() => setPage(p)}
                                >{p}</button>
                            ))}

                        <button className="br-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>›</button>
                        <button className="br-page-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>

                        <span className="br-page-info">Page {page} of {totalPages}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BidsRecords;
